/**
 * Fill timesheet — an admin enters hours for one employee directly, without
 * the employee having to submit anything, then submits and approves them.
 *
 *   1. Search for and pick an employee.
 *   2. Choose a week or a month and type hours into the same category grid
 *      employees use (each cell saves on blur).
 *   3. "Submit and approve" → manager name, hourly rate and, optionally,
 *      release the employee's Excel/PDF download in the same step.
 *
 * Approved weeks are locked: the grid goes read-only when every week shown is
 * approved, and the server refuses edits to an approved week inside a month.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";

import { Avatar } from "@m2s/design-system";

import { DesktopOnly } from "@/components/DesktopOnly";
import { EmployeePicker } from "@/components/EmployeePicker";
import { SubmitOnBehalfModal } from "@/components/SubmitOnBehalfModal";
import { TimesheetGridView } from "@/components/TimesheetGridView";
import { api, extractErrorMessage } from "@/lib/api";
import type { AdminSubmitResult, EmployeeTimesheetView, Member } from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const ADMIN_ROLES = new Set(["admin"]);

type View = "week" | "month";

// ---------- date helpers ----------

function startOfWeek(d: Date): Date {
  // Monday-anchored to match the rest of the app.
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
}
function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function rangeLabel(view: View, from: Date, to: Date): string {
  if (view === "month") {
    return from.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }
  const md: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  return `${from.toLocaleDateString(undefined, md)} – ${to.toLocaleDateString(undefined, md)}, ${to.getFullYear()}`;
}
function hours(seconds: number): string {
  const h = seconds / 3600;
  return h % 1 === 0 ? String(h) : h.toFixed(2);
}
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function FillTimesheetPage() {
  return (
    <DesktopOnly
      title="Fill timesheet"
      blurb="Entering an employee's hours uses the full category grid, which needs more room than a phone screen offers."
    >
      <FillTimesheetImpl />
    </DesktopOnly>
  );
}

function FillTimesheetImpl() {
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));
  const queryClient = useQueryClient();

  const [userId, setUserId] = useState<string | null>(null);
  const [view, setView] = useState<View>("week");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [submitOpen, setSubmitOpen] = useState(false);
  // Bumped when a save is refused. Remounting the grid makes every cell
  // re-read the saved value — a cell only resets itself when that value
  // changes, so a refused edit would otherwise stay on screen.
  const [gridVersion, setGridVersion] = useState(0);

  const { from, to } = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(anchor);
      return { from: s, to: new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6) };
    }
    return {
      from: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
      to: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0),
    };
  }, [view, anchor]);
  const fromISO = fmtISO(from);
  const toISO = fmtISO(to);

  // Feedback is keyed by employee + period so it doesn't linger after a switch.
  const rangeKey = `${userId ?? ""}|${fromISO}|${toISO}`;
  const [banner, setBanner] = useState<{ key: string; kind: "info" | "error"; text: string } | null>(
    null,
  );

  const membersQuery = useQuery({
    queryKey: ["members"],
    queryFn: async () => (await api.get<Member[]>("/admin/members")).data,
    enabled: isAdmin,
  });

  // Same endpoint + cache key as the Timesheet dashboard's single-employee view.
  const employeeQuery = useQuery({
    queryKey: ["admin", "employee-timesheet", userId, fromISO, toISO],
    queryFn: async () =>
      (
        await api.get<EmployeeTimesheetView>(
          `/admin/timesheets/employee/${userId}?from=${fromISO}&to=${toISO}`,
        )
      ).data,
    enabled: isAdmin && userId !== null,
  });

  const fillCell = useMutation({
    mutationFn: async (vars: { category: string; work_date: string; hours: number }) =>
      (await api.put(`/admin/timesheets/employee/${userId}/cell`, vars)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "employee-timesheet"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "team-timesheet"] });
      queryClient.invalidateQueries({ queryKey: ["admin-timesheet-submissions"] });
    },
    onError: (err) => {
      setBanner({
        key: rangeKey,
        kind: "error",
        text: extractErrorMessage(err, "Could not save that cell."),
      });
      // Put the saved value back on screen instead of the refused one.
      setGridVersion((v) => v + 1);
      queryClient.invalidateQueries({ queryKey: ["admin", "employee-timesheet"] });
    },
  });

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center" }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Admin only</h1>
        <p style={{ color: "var(--body)", lineHeight: 1.6 }}>
          Filling in someone else's timesheet is limited to workspace admins.
        </p>
      </div>
    );
  }

  const data = employeeQuery.data;
  const weeks = data?.weeks ?? [];
  const approved = weeks.filter((w) => w.status === "approved").length;
  const pending = weeks.filter((w) => w.status === "submitted").length;
  const rejected = weeks.filter((w) => w.status === "rejected").length;
  const notSubmitted = weeks.length - approved - pending - rejected;
  const allApproved = weeks.length > 0 && approved === weeks.length;

  const step = (dir: -1 | 1) =>
    setAnchor((d) =>
      view === "week"
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * dir)
        : new Date(d.getFullYear(), d.getMonth() + dir, 1),
    );

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Fill timesheet
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Enter an employee's hours yourself, then submit and approve them in one step. The
          employee doesn't need to submit anything.
        </p>
      </header>

      <div style={toolbar}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={fieldHint}>Employee</span>
          <EmployeePicker
            members={membersQuery.data ?? []}
            value={userId}
            onChange={setUserId}
            placeholder="Search by name, email or job ID"
          />
        </div>

        <span style={{ flex: 1 }} />

        <div role="group" aria-label="Period" style={segmented}>
          {(["week", "month"] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => {
                setView(v);
                setAnchor(new Date());
              }}
              style={segmentBtn(view === v)}
            >
              {v === "week" ? "Week" : "Month"}
            </button>
          ))}
        </div>
        <button type="button" aria-label="Previous" onClick={() => step(-1)} style={navBtn}>
          ‹
        </button>
        <span style={dateLabel}>{rangeLabel(view, from, to)}</span>
        <button type="button" aria-label="Next" onClick={() => step(1)} style={navBtn}>
          ›
        </button>
        <button type="button" onClick={() => setAnchor(new Date())} style={navBtn}>
          {view === "week" ? "This week" : "This month"}
        </button>
      </div>

      {userId === null ? (
        <div style={emptyState}>
          Choose an employee to start. You can search by name, email or job ID.
        </div>
      ) : employeeQuery.isLoading || !data ? (
        <p style={{ color: "var(--mute)" }}>
          {employeeQuery.isError ? "Could not load this timesheet." : "Loading…"}
        </p>
      ) : (
        <>
          <div style={empRow}>
            <Avatar initials={initialsOf(data.display_name)} title={data.display_name} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{data.display_name}</div>
              <div style={{ fontSize: 12, color: "var(--mute)" }}>
                {data.email}
                {data.job_id ? ` · ${data.job_id}` : ""}
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 12, color: "var(--mute)" }}>
              <div>
                Active{" "}
                <span style={{ color: "var(--ink)", fontFamily: "var(--font-mono)" }}>
                  {hours(data.billable_seconds)}h
                </span>{" "}
                · Total{" "}
                <span style={{ color: "var(--ink)", fontFamily: "var(--font-mono)" }}>
                  {hours(data.total_seconds)}h
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSubmitOpen(true)}
              disabled={allApproved || weeks.length === 0}
              title={
                allApproved
                  ? "Every week in this period is already approved."
                  : "Submit and approve these hours"
              }
              style={{
                ...primaryBtn,
                opacity: allApproved ? 0.5 : 1,
                cursor: allApproved ? "not-allowed" : "pointer",
              }}
            >
              Submit and approve
            </button>
          </div>

          <p style={{ fontSize: 12, color: "var(--mute)", margin: "0 0 8px" }}>
            {weeks.length} week{weeks.length === 1 ? "" : "s"} in this period: {approved} approved,{" "}
            {pending} awaiting approval, {rejected} rejected, {notSubmitted} not submitted.
            {allApproved
              ? " Approved weeks are locked, so the grid is read-only."
              : approved > 0
                ? " Approved weeks are locked; edits to them are refused."
                : " Type hours into any cell; each cell saves when you leave it."}
          </p>

          {banner && banner.key === rangeKey && (
            <p
              role={banner.kind === "error" ? "alert" : "status"}
              style={banner.kind === "error" ? errorBox : noticeBox}
            >
              <span style={{ flex: 1 }}>{banner.text}</span>
              <button type="button" onClick={() => setBanner(null)} style={dismissBtn}>
                Dismiss
              </button>
            </p>
          )}

          <TimesheetGridView
            key={gridVersion}
            from={from}
            to={to}
            rows={data.grid_rows}
            totalsByDay={data.totals_by_day}
            loading={false}
            readOnly={allApproved}
            onCellChange={(category, workDate, h) =>
              fillCell.mutate({ category, work_date: workDate, hours: h })
            }
          />
        </>
      )}

      {submitOpen && data && (
        <SubmitOnBehalfModal
          employee={{ user_id: data.user_id, display_name: data.display_name }}
          from={fromISO}
          to={toISO}
          weeks={weeks}
          totalSeconds={data.total_seconds}
          billableSeconds={data.billable_seconds}
          onClose={() => setSubmitOpen(false)}
          onDone={(r: AdminSubmitResult) => {
            setSubmitOpen(false);
            const parts = [
              `Submitted and approved ${r.approved_weeks} week${r.approved_weeks === 1 ? "" : "s"} for ${data.display_name}.`,
            ];
            if (r.download_released_weeks) parts.push("Their download is released.");
            if (r.skipped_empty) parts.push(`${r.skipped_empty} with no hours skipped.`);
            if (r.skipped_locked) parts.push(`${r.skipped_locked} already approved.`);
            setBanner({ key: rangeKey, kind: "info", text: parts.join(" ") });
          }}
        />
      )}
    </div>
  );
}

// ---------- styles ----------

const toolbar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  padding: 12,
  marginBottom: 16,
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
};

const fieldHint: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
};

const segmented: CSSProperties = {
  display: "inline-flex",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  overflow: "hidden",
};

function segmentBtn(active: boolean): CSSProperties {
  return {
    background: active ? "var(--ink)" : "var(--canvas)",
    color: active ? "var(--on-primary)" : "var(--ink)",
    border: "none",
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };
}

const navBtn: CSSProperties = {
  background: "var(--canvas)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dateLabel: CSSProperties = {
  minWidth: 150,
  textAlign: "center",
  fontSize: 13,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const emptyState: CSSProperties = {
  padding: "48px 16px",
  textAlign: "center",
  color: "var(--mute)",
  fontSize: 14,
  border: "1px dashed var(--hairline)",
  borderRadius: 8,
};

const empRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: 12,
  marginBottom: 12,
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  background: "var(--canvas)",
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "1px solid var(--ink)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
};

const noticeBox: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontSize: 13,
  color: "var(--ink)",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 12px",
  margin: "0 0 8px",
};

const errorBox: CSSProperties = {
  ...noticeBox,
  color: "var(--danger)",
  background: "color-mix(in srgb, var(--danger) 8%, transparent)",
  border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)",
};

const dismissBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--mute)",
  fontSize: 12,
  cursor: "pointer",
  padding: 0,
  fontFamily: "var(--font-sans)",
};
